import { GetStaticPaths, GetStaticProps } from "next";
import { FunctionComponent } from "react";
import {
  SubscriptionPreviewType,
  SubscriptionCollectionsPreview,
  SuperHero,
} from "@bluebottlecoffee/design-system";
import sanityClient from "../../../../lib/sanity-client";
import renderModule from "../../../../lib/render-module";
import { toSuperHeroProps } from "../../../../lib/transformers/super-hero";
import { LayoutWrapper } from "../../../../components/LayoutWrapper";
import {
  debugPrintBuilder,
  getDefaultStaticPathsMiniCart,
  getDefaultStaticPropsMiniCart,
  isDefined,
} from "../../../../lib/utils";
import { CartBaseProps } from "../../../../lib/sanity/shared";
import {
  SanityQueryType,
  buildSanityQuery as buildSubscriptionQuery,
  SanitySubscriptionPage,
  QueryKey,
  SanitySubscriptionCollectionsPreviewModule,
  SubscriptionCollectionsPreviewCopy,
} from "../../../../lib/sanity/subscription-queries";
import { toCopyObject } from "../../../../lib/sanity/translation-group-queries";
import { useCart } from "../../../../lib/chord/packages/web/src/hooks/actions/use-cart";
import { toSubscriptionPreview } from "../../../../lib/transformers/subscription-preview";
import { MiniCartDialog } from "../../../../components/MiniCartDialog";
import { ChordUseCartReturnType } from "../../../../lib/chord/types";

type SubscriptionNavProps = {
  subscriptionPreviews: SubscriptionPreviewType[];
};

interface PageData
  extends CartBaseProps,
    SanityQueryType,
    SubscriptionNavProps {}

export const getStaticProps: GetStaticProps<PageData> = async (context) => {
  const { props: defaultProps } = await getDefaultStaticPropsMiniCart(context);

  const { lang, region } = defaultProps;

  const query = buildSubscriptionQuery(lang);

  const sanityQueryResult = await sanityClient().fetch<SanityQueryType>(query);

  const subscriptionPage = sanityQueryResult[QueryKey.SUBSCRIPTION_PAGE];

  const copy = toCopyObject<SubscriptionCollectionsPreviewCopy>(
    lang,
    sanityQueryResult[QueryKey.SUBSCRIPTION_COLLECTIONS_PREVIEW_MODULE_COPY],
  );

  const module: SanitySubscriptionCollectionsPreviewModule | undefined = (
    subscriptionPage.modules as any[]
  ).find((m) => m?._type === "subscriptionCollectionsPreviewModule");

  const subscriptionPreviews: SubscriptionPreviewType[] = await Promise.all(
    module?.shopCollections?.map((sanityShopCollection) =>
      toSubscriptionPreview({ lang, region, sanityShopCollection, copy }),
    ) || [],
  );

  return {
    props: {
      subscriptionPreviews: subscriptionPreviews.filter(
        (shopCollection) => shopCollection.cards.filter(isDefined).length > 0,
      ),
      ...sanityQueryResult,
      ...defaultProps,
    },
  };
};

export const getStaticPaths: GetStaticPaths = getDefaultStaticPathsMiniCart;

function validate(subscriptionPage: SanitySubscriptionPage) {
  if (!subscriptionPage?.title) {
    throw Error("Subscription Page must have a title.");
  }
}

const SubscriptionPage: FunctionComponent<PageData> = ({
  subscriptionPreviews: staticSubscriptionPreviews,
  subscriptionPage,
  ...miniCartLayoutWrapperProps
}) => {
  validate(subscriptionPage);
  const { region, lang } = miniCartLayoutWrapperProps;

  const { subscribeProduct }: ChordUseCartReturnType = useCart();

  const subscriptionPreviews = staticSubscriptionPreviews.map(
    ({ cards, ...rest }) => ({
      ...rest,
      cards: cards.map((card) => ({
        ...card,
        selection: { ...card.selection, chordSubscribe: subscribeProduct },
      })),
    }),
  );

  const miniCartDialog = (
    <MiniCartDialog
      miniCartCopy={miniCartLayoutWrapperProps.cart}
      region={region}
      lang={lang}
    />
  );

  return (
    <LayoutWrapper
      {...{
        ...miniCartLayoutWrapperProps,
        pageTitle: subscriptionPage.title[lang],
        metaTags: subscriptionPage.metaTags,
        slideOutContent: [miniCartDialog],
      }}
    >
      {subscriptionPage.hero?.length > 0 && (
        <SuperHero {...toSuperHeroProps(subscriptionPage.hero, region, lang)} />
      )}
      {subscriptionPage.modules?.length > 0 &&
        subscriptionPage.modules.map((m) => {
          if (m._type === "subscriptionCollectionsPreviewModule") {
            const { title, subtitle } =
              m as SanitySubscriptionCollectionsPreviewModule;

            if (
              subscriptionPreviews.length === 0 ||
              subscriptionPreviews.every(
                (preview) => preview.cards.length === 0,
              )
            ) {
              throw Error(
                "A SubscriptionCollectionPreviewModule was provided on the" +
                  " Subscriptions Page, but no Shop Collections listed on the" +
                  " module had any subscribable products also referencing" +
                  " these Shop Collections. Either assign subscribable" +
                  " products to the Shop Collections listed on the" +
                  " Subscriptions Page for this module or remove the" +
                  " SubscriptionCollectionPreviewModule from the" +
                  " Subscriptions Page.",
              );
            }

            return (
              <div id="subscriptions-section">
                <SubscriptionCollectionsPreview
                  title={title[lang]}
                  subtitle={subtitle[lang]}
                  subscriptionPreviews={subscriptionPreviews}
                  debugPrint={debugPrintBuilder("SubscriptionTabBarView: ")}
                />
              </div>
            );
          }
          return renderModule(m, region, lang);
        })}
    </LayoutWrapper>
  );
};

export default SubscriptionPage;
